import { jenks } from 'simple-statistics'

export type Bucket = {
  key: number
  counts: number
}

export default class Histogram {
  total: number
  buckets: Bucket[]
  _min: number
  _max: number
  _flattened: number[] | undefined
  _statsMinMax: [number, number] | undefined

  // TODO: this constructor is not ideal in TypeScript. It would
  // be better if we could auto-detect data type or simply offer two
  // distinct static methods to create Histogram instances.
  constructor(
    data: number[] | Bucket[],
    isBuckets: boolean | undefined = false
  ) {
    this.total = 0
    this._min = Infinity
    this._max = -Infinity
    this._flattened = undefined
    this._statsMinMax = undefined
    this.buckets = []

    if (isBuckets) {
      this.initializeFromBuckets(data as Bucket[])
    } else {
      this.initializeFromNumbers(data as number[])
    }

    this.buckets.sort(function (a, b) {
      return a.key - b.key
    })
  }

  length() {
    return this.buckets.length
  }

  min() {
    return this._min
  }

  max() {
    return this._max
  }

  // Note: will not give exact median, but a value from the bucket
  // closest to the median.
  median() {
    return this.percentile(50)
  }

  count(threshold?: number | undefined) {
    const filterThreshold = threshold == null ? this._max : threshold
    return this.buckets
      .filter(({ key }) => key <= filterThreshold)
      .reduce((count, { counts }) => count + counts, 0)
  }

  sum() {
    return this.buckets.reduce((sum, { key, counts }) => sum + key * counts, 0)
  }

  average() {
    return this.sum() / this.total
  }

  // This method is only kept for backwards compatibility
  equalThresholds() {
    return this.equalIntervalBreaks(2)
  }

  percentile(p: number) {
    const flattenedBucket = this.flattened()
    const i = (flattenedBucket.length - 1) * (p / 100)
    const i0 = Math.floor(i)
    const c1 = flattenedBucket[i0]
    const c2 = flattenedBucket[i0 + 1]
    return c1 + (c2 - c1) * (i - i0)
  }

  flattened() {
    if (this._flattened) {
      return this._flattened
    } else {
      this._flattened = flatten(this.buckets)
      return this._flattened
    }
  }

  findBucketIndex(v: number) {
    if (this.length() === 0) return -1
    if (v < this.buckets[0].key) return 0
    if (v >= this.buckets[this.buckets.length - 1].key)
      return this.buckets.length - 1
    return this.buckets.findIndex(b => b.key > v) - 1
  }

  statsMinMax() {
    if (this._statsMinMax) {
      return this._statsMinMax
    } else {
      const pc = [this.percentile(1), this.percentile(99)]
      const hiPcIndex = Math.min(
        this.findBucketIndex(pc[1]) + 1,
        this.buckets.length - 1
      )
      this._statsMinMax = [pc[0], this.buckets[hiPcIndex].key]
      return this._statsMinMax
    }
  }

  scale(f: number | ((v: number) => number)) {
    const buckets = this.buckets.map(({ key, counts }) => {
      const v = key
      const scaledV = typeof f === 'function' ? f(v) : v * f
      return { key: scaledV, counts }
    })
    // Remove buckets with duplicate keys
    for (let i = 1; i < buckets.length; ) {
      if (buckets[i - 1].key === buckets[i].key) {
        buckets[i - 1].counts += buckets[i].counts
        buckets.splice(i, 1)
      } else {
        i++
      }
    }
    return new Histogram(buckets, true)
  }

  filter(bucketPredicate: (bucket: Bucket) => boolean) {
    const nextBuckets = this.buckets.filter(bucketPredicate)
    return new Histogram(nextBuckets, true)
  }

  equalCountBreaks(n: number) {
    return Array.from({ length: n }).map((_, i) =>
      this.percentile((100 / n) * i)
    )
  }

  equalIntervalBreaks(n: number) {
    const [min, max] = this.statsMinMax()
    const equalInterval = (max - min) / (n + 1)

    return Array.from({ length: n }).map(
      (_, i) => equalInterval * (i + 1) + min
    )
  }

  thin(
    n: number,
    addEmpty: boolean,
    mode: 'equal_count' | 'equal_interval' | 'natural_breaks' = 'equal_interval'
  ): Histogram {
    if (this.buckets.length <= n) {
      // Just clone the current histogram
      return this.scale(1)
    }

    switch (mode) {
      case 'equal_interval': {
        const min = this.min()
        const width = this.max() - min
        const bucketSize = width / n

        const next = this.scale(function (v) {
          return (
            Math.min(n - 1, Math.floor((v - min) / bucketSize)) * bucketSize +
            min
          )
        })

        next._min = this.min()
        next._max = this.max()

        if (addEmpty) {
          for (let i = 0; i < n; i++) {
            const x = min + bucketSize * i
            if (!next.buckets.find(b => b.key === x)) {
              next.buckets.push({ key: x, counts: 0 })
            }
          }

          next.buckets.sort(function (a, b) {
            return a.key - b.key
          })
        }

        return next
      }
      case 'equal_count': {
        const perBucket = this.total / n
        const nextBuckets = Array.from({ length: n }).map((_, i) => ({
          key: 0,
          counts: 0,
        }))
        let bucketIndex = 0
        let count = 0
        nextBuckets[bucketIndex].key = this.buckets[0].key
        for (let i = 0; i < this.buckets.length; i++) {
          const nItems = perBucket * (bucketIndex + 1)
          if (count >= nItems) {
            bucketIndex++
            nextBuckets[bucketIndex].key = this.buckets[i].key
          }
          nextBuckets[bucketIndex].counts += this.buckets[i].counts
          count += this.buckets[i].counts
        }

        return new Histogram(nextBuckets, true)
      }
      case 'natural_breaks': {
        const breaks = jenks(this.flattened(), n)
        const nextBuckets = Array.from({ length: n }).map((_, i) => ({
          key: breaks[i],
          counts: 0,
        }))

        let bucketIndex = 0
        for (let i = 0; i < this.buckets.length; i++) {
          if (
            bucketIndex < n - 1 &&
            this.buckets[i].key >= nextBuckets[bucketIndex + 1].key
          ) {
            bucketIndex++
          }
          nextBuckets[bucketIndex].counts += this.buckets[i].counts
        }

        return new Histogram(nextBuckets, true)
      }
    }
  }

  /**
   * Returns the histogram data in the format HistogramChart
   *  with outliers removed
   */
  toDataFiltered() {
    const [min, max] = this.statsMinMax()
    return this.toData().filter(x => x.start >= min && x.start < max)
  }

  /**
   * Returns the histogram data in the format HistogramChart accepts
   */
  toData() {
    return this.buckets.map((b, i) => ({
      start: b.key,
      end: i < this.buckets.length - 1 ? this.buckets[i + 1].key : this.max(),
      count: b.counts,
    }))
  }

  static fromNumPyHistogram(histogram: [number[], number[]]) {
    const [counts, keys] = histogram
    return new Histogram(
      counts.map((y, i) => ({ key: keys[i], counts: y })),
      true
    )
  }

  initializeFromBuckets(buckets: Bucket[]) {
    this.buckets = buckets
    this.total = buckets.reduce((sum, bucket) => {
      return (sum += bucket.counts)
    }, 0)

    this._min = Math.min(...buckets.map(bucket => bucket.key))
    this._max = Math.max(...buckets.map(bucket => bucket.key))
  }

  initializeFromNumbers(data: number[]) {
    const bucketCounts: Record<string, number> = {}

    for (let i = 0; i < data.length; i++) {
      const d = data[i]
      if (!bucketCounts[d]) {
        bucketCounts[d] = 1
      } else {
        bucketCounts[d]++
      }
      this.total++
    }

    this.buckets = Object.keys(bucketCounts).reduce((a, key) => {
      const n = Number(key)
      const counts = bucketCounts[key]
      a.push({
        key: n,
        counts,
      })
      this._min = Math.min(this._min, n)
      this._max = Math.max(this._max, n)
      return a
    }, [] as Bucket[])
  }
}

function flatten(buckets: Bucket[]) {
  const values = []
  for (let i = 0; i < buckets.length; i++) {
    const val = buckets[i]
    for (let j = 0; j < val.counts; j++) {
      values.push(val.key)
    }
  }
  return values
}
